import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';

/**
 * TreatmentGoalsTab
 *
 * Handles Treatment Goals section for MH triage assessments (ReportTypeID 2002: Triage)
 * Maps to TreatmentGoals table (TG1, TG2) and TriageDetails/Report tables
 * This is a per-injury tab (TabPerInjury: TRUE)
 */

const TreatmentGoalsTab = ({ reportId, api, patientInfo, onSave , isPublished}) => {
  // Form state
  const [tg1, setTg1] = useState('');
  const [tg2, setTg2] = useState('');
  const [motivationToChange, setMotivationToChange] = useState('');
  const [disableReport, setDisableReport] = useState(null);
  const [closeCase, setCloseCase] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});

  useEffect(() => {
    loadTreatmentGoalsData();
  }, [reportId]);

  const loadTreatmentGoalsData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-treatment-goals`);

      if (response.ok) {
        const data = await response.json();
        setTg1(data.tG1 || '');
        setTg2(data.tG2 || '');
        setMotivationToChange(data.motivationToChange || '');
        setDisableReport(data.disableReport);
        setCloseCase(data.closeCase);

        // Set loaded fields as 'saved'
        const initialSavingStates = {};
        if (data.tG1) initialSavingStates['Tg1'] = 'saved';
        if (data.tG2) initialSavingStates['Tg2'] = 'saved';
        if (data.motivationToChange) initialSavingStates['MotivationToChange'] = 'saved';
        if (data.disableReport !== null) initialSavingStates['DisableReport'] = 'saved';
        if (data.closeCase !== null) initialSavingStates['CloseCase'] = 'saved';
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading treatment goals data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-treatment-goals/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify(value)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    const fieldValues = {
      'Tg1': tg1,
      'Tg2': tg2,
      'MotivationToChange': motivationToChange,
      'DisableReport': disableReport,
      'CloseCase': closeCase
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000);

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    saveField(fieldName, value);
  };

  const handleBooleanChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading treatment goals...</p>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="patient-summary">
        <div><strong>Case ID:</strong> {patientInfo?.caseId}</div>
        <div><strong>Name:</strong> {patientInfo?.name}</div>
        <div><strong>DOB:</strong> {patientInfo?.dateOfBirth}</div>
        <div><strong>Address:</strong> {patientInfo?.address}</div>
      </div>

      <div className="form-section">
        <h3>Treatment Goals</h3>

        <p>Goals should be <strong>SMART</strong> (specific, measurable, achievable, realistic and time limited)</p>

        <div className="form-group">
        
          <label htmlFor="tg1">
            Treatment goal 1:
            {getSaveIndicator('Tg1')}
          </label>
          <textarea
            id="tg1"
            value={tg1}
            onChange={(e) => handleFieldChange('Tg1', e.target.value, setTg1)}
            onBlur={() => handleFieldBlur('Tg1', tg1)}
            rows="4"
            style={{ width: '100%' }}
            placeholder="Enter the first SMART treatment goal for the patient"
          />
        </div>

        <div className="form-group">
          <label htmlFor="tg2">
            Treatment goal 2:
            {getSaveIndicator('Tg2')}
          </label>
          <textarea
            id="tg2"
            value={tg2}
            onChange={(e) => handleFieldChange('Tg2', e.target.value, setTg2)}
            onBlur={() => handleFieldBlur('Tg2', tg2)}
            rows="4"
            style={{ width: '100%' }}
            placeholder="Enter the second SMART treatment goal for the patient"
          />
        </div>

        <div className="form-group">
          <label htmlFor="motivationToChange">
            Motivation to change including any identified barriers:
            {getSaveIndicator('MotivationToChange')}
          </label>
          <textarea
            id="motivationToChange"
            value={motivationToChange}
            onChange={(e) => handleFieldChange('MotivationToChange', e.target.value, setMotivationToChange)}
            onBlur={() => handleFieldBlur('MotivationToChange', motivationToChange)}
            rows="4"
            style={{ width: '100%' }}
            placeholder="Describe the patient's motivation to change and any barriers identified"
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Report Actions</h3>

        <div className="form-group">
          <label className="inline-label">
            <strong>Disable Report: <span className="required">*</span></strong>
            {getSaveIndicator('DisableReport')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="disableReport"
                checked={disableReport === true}
                onChange={() => handleBooleanChange('DisableReport', true, setDisableReport)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="disableReport"
                checked={disableReport === false}
                onChange={() => handleBooleanChange('DisableReport', false, setDisableReport)}
              />
              No
            </label>
          </div>
          <div className="field-help">
            Disabling the report will prevent further edits
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="closeCase">
            Do you want to close the case?
            {getSaveIndicator('CloseCase')}
          </label>
          <select
            id="closeCase"
            value={closeCase === null ? '' : (closeCase ? 'true' : 'false')}
            onChange={(e) => {
              const value = e.target.value === '' ? null : e.target.value === 'true';
              handleBooleanChange('CloseCase', value, setCloseCase);
            }}
            style={{ width: '250px' }}
          >
            <option value="">-- Select --</option>
            <option value="true">Yes - Close the case</option>
            <option value="false">No - Keep the case open</option>
          </select>
          <div className="field-help">
            Closing the case will end treatment for this patient
          </div>
        </div>
      </div>

      {(disableReport === true || closeCase === true) && (
        <div className="warning-message">
          <strong>⚠ Warning:</strong>
          {disableReport === true && ' You have selected to disable this report.'}
          {closeCase === true && ' You have selected to close this case.'}
          {' '}Please ensure this is correct before proceeding.
        </div>
      )}
      </fieldset>

    </div>
  );
};

export default TreatmentGoalsTab;
